import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { generateRequestUri } from "../../common/service";
import { AppContext } from "../../context";
import {
  deleteStoreBatch,
  getStoreBatch,
  getStoreBatches,
  markStoreBatchReceived,
  moveStoreBatchToProduction,
} from "../production/service";
import { getSignedToken } from "../service";
import { toErrorText } from "./storeSalesDashboardHelpers";

const formatDateTime = (value) => {
  if (!value) return "—";
  const t = Date.parse(value);
  if (Number.isNaN(t)) return "—";

  return new Date(t).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPriorityDate = (value) => {
  if (!value) return "—";
  const t = Date.parse(value);
  if (Number.isNaN(t)) return "—";

  return new Date(t).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const BATCH_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "production", label: "Production" },
  { value: "completed", label: "Completed" },
];

const StoreOpsBatchesSection = () => {
  const { currentUser } = useContext(AppContext);
  const [statusFilter, setStatusFilter] = useState("draft");
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getStoreBatches();
      setBatches(rows);
    } catch (err) {
      setBatches([]);
      setError(toErrorText(err, "Unable to load batches."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBatch = useCallback(async (batchId) => {
    if (!batchId) return;
    setBatchLoading(true);
    setBatchError(null);
    try {
      const payload = await getStoreBatch(batchId);
      setSelectedBatch(payload);
    } catch (err) {
      setSelectedBatch(null);
      setBatchError(toErrorText(err, "Unable to load batch details."));
    } finally {
      setBatchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    if (selectedBatchId == null) {
      setSelectedBatch(null);
      setBatchError(null);
      return;
    }

    loadBatch(selectedBatchId);
  }, [loadBatch, selectedBatchId]);

  const closeBatchModal = () => {
    if (actionLoading) return;
    setSelectedBatchId(null);
  };

  const handleMoveToProduction = async () => {
    if (!selectedBatchId) return;
    setActionLoading(true);
    setBatchError(null);
    try {
      await moveStoreBatchToProduction(selectedBatchId);
      await Promise.all([loadBatch(selectedBatchId), loadBatches()]);
    } catch (err) {
      setBatchError(toErrorText(err, "Unable to move batch to production."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatchId || !selectedBatch) return;

    const confirmed = window.confirm(
      `Delete ${selectedBatch.name}? Its part items will return to Planning.`
    );
    if (!confirmed) return;

    setActionLoading(true);
    setBatchError(null);
    try {
      await deleteStoreBatch(selectedBatchId);
      setSelectedBatchId(null);
      setSelectedBatch(null);
      await loadBatches();
    } catch (err) {
      setBatchError(toErrorText(err, "Unable to delete batch."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkReceived = async () => {
    if (!selectedBatchId) return;
    setActionLoading(true);
    setBatchError(null);
    try {
      await markStoreBatchReceived(selectedBatchId);
      await Promise.all([loadBatch(selectedBatchId), loadBatches()]);
    } catch (err) {
      setBatchError(toErrorText(err, "Unable to mark batch as received."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadArtifacts = async () => {
    if (!selectedBatchId) return;
    setActionLoading(true);
    setBatchError(null);
    try {
      const { token } = await getSignedToken({
        userId: currentUser.id,
        resource: "batch_artifacts",
      });
      window.open(
        generateRequestUri({
          url: `/store/batches/${selectedBatchId}/artifacts`,
          params: { signed: token },
        }),
        "_blank",
        "noreferrer"
      );
    } catch (err) {
      setBatchError(toErrorText(err, "Unable to download artifacts."));
    } finally {
      setActionLoading(false);
    }
  };

  const batchItems = useMemo(() => {
    if (!selectedBatch) return [];
    if (!Array.isArray(selectedBatch.part_items)) return [];

    return selectedBatch.part_items;
  }, [selectedBatch]);

  const filteredBatches = useMemo(
    () => batches.filter((batch) => batch.status === statusFilter),
    [batches, statusFilter]
  );

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        statusFilter === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [statusFilter]
  );

  const isDraftBatch = selectedBatch?.status === "draft";
  const isProductionBatch = selectedBatch?.status === "production";
  const canDownloadArtifacts =
    isProductionBatch || selectedBatch?.status === "completed";

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted small me-2">Status:</span>
        {BATCH_STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={pillClassName(option.value)}
            onClick={() => setStatusFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="py-5 text-center text-muted">
          <Spinner className="me-2" />
          Loading...
        </div>
      ) : filteredBatches.length === 0 ? (
        <p className="text-muted mb-0">No batches found.</p>
      ) : (
        <div className="row row-cols-1 row-cols-md-3 g-3">
          {filteredBatches.map((batch) => (
            <div key={batch.id} className="col">
              <Card
                className="h-100 shadow-sm"
                role="button"
                onClick={() => setSelectedBatchId(batch.id)}
              >
                <CardBody>
                  <div className="fw-semibold text-dark">{batch.name}</div>
                  <div className="small text-muted mt-2">
                    <div>Factory: {batch.factory.name}</div>
                    <div className="text-capitalize">
                      Status: {batch.status}
                    </div>
                    <div>Items: {batch.items_count}</div>
                    <div>Received: {formatDateTime(batch.received_at)}</div>
                  </div>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={selectedBatchId != null}
        toggle={closeBatchModal}
        centered
        size="xl"
      >
        <ModalHeader toggle={closeBatchModal}>Batch details</ModalHeader>
        <ModalBody>
          {batchError && (
            <Alert color="danger" fade={false}>
              {batchError}
            </Alert>
          )}
          {batchLoading ? (
            <div className="py-5 text-center text-muted">
              <Spinner className="me-2" />
              Loading...
            </div>
          ) : !selectedBatch ? (
            <p className="text-muted mb-0">Batch details unavailable.</p>
          ) : (
            <>
              <div className="small text-muted mb-3">
                <div className="fw-semibold text-dark mb-1">
                  {selectedBatch.name}
                </div>
                <div>Factory: {selectedBatch.factory.name}</div>
                <div className="text-capitalize">
                  Status: {selectedBatch.status}
                </div>
                <div>Received: {formatDateTime(selectedBatch.received_at)}</div>
              </div>
              <div className="table-responsive">
                <Table
                  bordered
                  hover
                  responsive
                  className="mb-0 store-sales-table"
                >
                  <thead className="store-sales-table__head">
                    <tr>
                      <th>Project</th>
                      <th>Description</th>
                      <th>Thickness</th>
                      <th>FB Length</th>
                      <th>FB Width</th>
                      <th>Panel Name</th>
                      <th>Remark</th>
                      <th>Assembly Label</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="text-muted text-center py-4"
                        >
                          No part items found for this batch.
                        </td>
                      </tr>
                    ) : (
                      batchItems.map((row) => (
                        <tr key={row.id}>
                          <td>{row.order_identifier}</td>
                          <td>{row.description}</td>
                          <td>{row.thickness}</td>
                          <td>{row.fb_length}</td>
                          <td>{row.fb_width}</td>
                          <td>{row.panel_name}</td>
                          <td>{row.remark || "-"}</td>
                          <td>{row.assembly_label}</td>
                          <td>{formatPriorityDate(row.priority)}</td>
                          <td className="text-capitalize">{row.status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {isDraftBatch && (
            <Button
              color="danger"
              outline
              className="me-auto"
              disabled={actionLoading || batchLoading || !selectedBatchId}
              onClick={handleDeleteBatch}
            >
              Delete
            </Button>
          )}
          <Button
            color="secondary"
            outline
            disabled={actionLoading || batchLoading}
            onClick={closeBatchModal}
          >
            Close
          </Button>
          {isDraftBatch && (
            <Button
              color="primary"
              disabled={actionLoading || batchLoading || !selectedBatchId}
              onClick={handleMoveToProduction}
            >
              Move to Production
            </Button>
          )}
          {canDownloadArtifacts && (
            <Button
              color="success"
              disabled={actionLoading || batchLoading || !selectedBatchId}
              onClick={handleDownloadArtifacts}
            >
              Download Artifacts
            </Button>
          )}
          {isProductionBatch &&
            !batchLoading &&
            selectedBatch &&
            !selectedBatch.received_at && (
              <Button
                color="primary"
                disabled={actionLoading || !selectedBatchId}
                onClick={handleMarkReceived}
              >
                Mark as Received
              </Button>
            )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StoreOpsBatchesSection;
